import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { DocManiaLogo, DocManiaLogoImage } from '../components/DocManiaLogo';
import { toast } from 'sonner';
import { Mail, Send, ArrowLeft, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [form, setForm] = useState({ full_name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const isValid = form.full_name.trim() && form.email.trim() && form.subject.trim() && form.message.trim()
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setSent(true);
        toast.success(data.message || 'Message sent successfully!');
      } else {
        toast.error(data.detail || 'Failed to send message. Please try again.');
      }
    } catch {
      toast.error('Unable to reach the server. Check your connection and try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="contact-nav-logo">
              <DocManiaLogoImage size={36} />
              <DocManiaLogo />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="font-medium">Login</Button>
              </Link>
              <Link to="/signup">
                <Button className="glow-button font-medium">Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-20 px-4">
        <div className="max-w-2xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>

          <div className="space-y-2 mb-8">
            <h1 className="text-3xl font-bold text-foreground" data-testid="contact-heading">Get Support</h1>
            <p className="text-muted-foreground">
              Have a question, issue, or support request? Send us a message and we'll get back to you.
            </p>
          </div>

          <div className="flex items-center gap-3 mb-8 p-4 rounded-xl bg-primary/5 border border-primary/10" data-testid="support-email-display">
            <Mail className="h-5 w-5 text-primary shrink-0" />
            <div>
              <p className="text-sm text-muted-foreground">You can also email us directly at</p>
              <a href="mailto:support@docmania.co" className="text-primary font-semibold hover:underline">
                support@docmania.co
              </a>
            </div>
          </div>

          {sent ? (
            <Card className="border-green-200 bg-green-50" data-testid="contact-success">
              <CardContent className="py-10 text-center space-y-4">
                <div className="h-14 w-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                  <Send className="h-6 w-6 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-foreground">Message Sent</h2>
                <p className="text-muted-foreground">Thanks for reaching out. We'll get back to you as soon as possible.</p>
                <Button variant="outline" onClick={() => { setSent(false); setForm({ full_name: '', email: '', subject: '', message: '' }); }}>
                  Send Another Message
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card data-testid="contact-form-card">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-foreground">Full Name *</label>
                      <input
                        name="full_name"
                        value={form.full_name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-foreground">Email Address *</label>
                      <input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="you@email.com"
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">Subject *</label>
                    <input
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      required
                      placeholder="What is this about?"
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      data-testid="contact-subject"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">Message *</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Describe your question or issue..."
                      className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                      data-testid="contact-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={!isValid || sending}
                    className="w-full h-11 glow-button font-medium"
                    data-testid="contact-submit"
                  >
                    {sending ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>
                    ) : (
                      <><Send className="mr-2 h-4 w-4" />Send Message</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <footer className="border-t border-border py-8 px-4 bg-white">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <DocManiaLogoImage size={24} />
            <DocManiaLogo size="small" />
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <a href="/privacy" className="hover:text-foreground transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-foreground transition-colors">Terms</a>
            <a href="/pricing" className="hover:text-foreground transition-colors">Pricing</a>
            <a href="/refund-policy" className="hover:text-foreground transition-colors">Refund Policy</a>
            <a href="/contact" className="hover:text-foreground transition-colors">Contact</a>
          </div>
          <p className="text-sm text-muted-foreground">&copy; {new Date().getFullYear()} DocMania</p>
        </div>
      </footer>
    </div>
  );
}

